import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Save, Type, FileText, Share2, Upload, Image } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getImageSrc(url) {
  if (!url) return '';
  if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

export default function ContentEditorPage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const heroImageRef = useRef(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { data } = await axios.get(`${API}/content`, { withCredentials: true });
      setContent(data);
    } catch (error) {
      toast.error('Failed to fetch content');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/content`, content, { withCredentials: true });
      toast.success('Content saved successfully!');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const updatePolicy = (index, value) => {
    const updated = [...content.booking_policy];
    updated[index] = value;
    setContent({ ...content, booking_policy: updated });
  };

  const addPolicyItem = () => {
    setContent({ ...content, booking_policy: [...content.booking_policy, ''] });
  };

  const removePolicyItem = (index) => {
    const updated = content.booking_policy.filter((_, i) => i !== index);
    setContent({ ...content, booking_policy: updated });
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, GIF, and WEBP images are allowed');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setContent({ ...content, hero_image: data.url });
      toast.success('Cover photo uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (heroImageRef.current) heroImageRef.current.value = '';
    }
  };

  if (loading || !content) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9C6A48]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="content-editor-page">
      <div className="mb-8">
        <h1 className="text-3xl font-light text-[#2B2623]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Content Editor
        </h1>
        <p className="text-[#756A63] mt-1">Edit your website content — hero section, booking policy, social links, and deposit amount</p>
      </div>

      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Hero Section */}
          <div className="bg-white rounded-xl border border-[#E3DACD] p-6">
            <h2 className="text-lg font-medium text-[#2B2623] mb-4 flex items-center gap-2">
              <Type className="w-5 h-5 text-[#9C6A48]" />
              Hero Section
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Subtitle Label</label>
                <Input
                  data-testid="hero-subtitle-input"
                  value={content.hero_subtitle}
                  onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })}
                  placeholder="Premium Hair Artistry"
                  className="border-[#E3DACD]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Main Title</label>
                <Textarea
                  data-testid="hero-title-input"
                  value={content.hero_title}
                  onChange={(e) => setContent({ ...content, hero_title: e.target.value })}
                  placeholder="Elevated&#10;Hair Artistry"
                  rows={2}
                  className="border-[#E3DACD]"
                />
                <p className="text-xs text-[#756A63] mt-1">Use new lines for line breaks in the heading</p>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Description</label>
                <Textarea
                  data-testid="hero-description-input"
                  value={content.hero_description}
                  onChange={(e) => setContent({ ...content, hero_description: e.target.value })}
                  placeholder="Experience the art of protective styling..."
                  rows={3}
                  className="border-[#E3DACD]"
                />
              </div>
            </div>
          </div>

          {/* Cover Photo */}
          <div className="bg-white rounded-xl border border-[#E3DACD] p-6">
            <h2 className="text-lg font-medium text-[#2B2623] mb-4 flex items-center gap-2">
              <Image className="w-5 h-5 text-[#9C6A48]" />
              Cover Photo
            </h2>
            <p className="text-sm text-[#756A63] mb-4">The main hero image displayed on the landing page</p>
            <div className="flex items-start gap-6">
              <div className="shrink-0">
                {content.hero_image ? (
                  <div className="w-40 h-28 rounded-xl overflow-hidden border border-[#E3DACD]">
                    <img
                      src={getImageSrc(content.hero_image)}
                      alt="Cover"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/320x200?text=Cover'; }}
                    />
                  </div>
                ) : (
                  <div className="w-40 h-28 rounded-xl bg-[#EDE7DD] flex items-center justify-center">
                    <Image className="w-8 h-8 text-[#9C6A48]" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <Button
                  type="button"
                  variant="outline"
                  className="border-[#E3DACD] hover:bg-[#9C6A48]/5"
                  onClick={() => heroImageRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-hero-image-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload Cover Photo'}
                </Button>
                <input
                  ref={heroImageRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={handleHeroImageUpload}
                  data-testid="hero-image-file-input"
                />
                <p className="text-xs text-[#756A63]">JPEG, PNG, GIF, WEBP up to 10MB</p>
                <div>
                  <label className="text-xs text-[#756A63] mb-1 block">Or paste image URL:</label>
                  <Input
                    data-testid="hero-image-url-input"
                    value={content.hero_image || ''}
                    onChange={(e) => setContent({ ...content, hero_image: e.target.value })}
                    placeholder="https://example.com/cover-photo.jpg"
                    className="border-[#E3DACD] text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Deposit & Payment */}
          <div className="bg-white rounded-xl border border-[#E3DACD] p-6">
            <h2 className="text-lg font-medium text-[#2B2623] mb-4">Deposit & Payment</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Deposit Amount (CAD $)</label>
                <Input
                  data-testid="deposit-amount-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={content.deposit_amount}
                  onChange={(e) => setContent({ ...content, deposit_amount: parseFloat(e.target.value) || 0 })}
                  className="border-[#E3DACD]"
                />
                <p className="text-xs text-[#756A63] mt-1">Amount customers must send via Interac e-Transfer to confirm their booking</p>
              </div>
            </div>
          </div>

          {/* Salon Address */}
          <div className="bg-white rounded-xl border border-[#E3DACD] p-6">
            <h2 className="text-lg font-medium text-[#2B2623] mb-4">Salon Address</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Address</label>
                <Input
                  data-testid="salon-address-input"
                  value={content.salon_address || ''}
                  onChange={(e) => setContent({ ...content, salon_address: e.target.value })}
                  placeholder="123 Main Street, City, Province, Postal Code"
                  className="border-[#E3DACD]"
                />
                <p className="text-xs text-[#756A63] mt-1">This address will be included in all confirmation and reminder emails sent to customers</p>
              </div>
            </div>
          </div>

          {/* Booking Policy */}
          <div className="bg-white rounded-xl border border-[#E3DACD] p-6">
            <h2 className="text-lg font-medium text-[#2B2623] mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#9C6A48]" />
              Booking Policy
            </h2>
            <div className="space-y-3">
              {content.booking_policy.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-[#756A63] text-sm shrink-0">#{index + 1}</span>
                  <Input
                    data-testid={`policy-item-${index}`}
                    value={item}
                    onChange={(e) => updatePolicy(index, e.target.value)}
                    placeholder="Policy item..."
                    className="border-[#E3DACD] flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removePolicyItem(index)}
                    className="text-red-400 hover:text-red-600 shrink-0"
                    data-testid={`remove-policy-btn-${index}`}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addPolicyItem}
                className="border-[#E3DACD] text-[#756A63]"
                data-testid="add-policy-btn"
              >
                + Add Policy Item
              </Button>
            </div>
          </div>

          {/* Social Media Links */}
          <div className="bg-white rounded-xl border border-[#E3DACD] p-6">
            <h2 className="text-lg font-medium text-[#2B2623] mb-4 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#9C6A48]" />
              Social Media
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Facebook Page URL</label>
                <Input
                  data-testid="facebook-url-input"
                  value={content.facebook_url}
                  onChange={(e) => setContent({ ...content, facebook_url: e.target.value })}
                  placeholder="https://facebook.com/braidsbyabby"
                  className="border-[#E3DACD]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Instagram Page URL</label>
                <Input
                  data-testid="instagram-url-input"
                  value={content.instagram_url}
                  onChange={(e) => setContent({ ...content, instagram_url: e.target.value })}
                  placeholder="https://instagram.com/braidsbyabby"
                  className="border-[#E3DACD]"
                />
              </div>
              <p className="text-xs text-[#756A63]">Leave empty to hide the icon on the website</p>
            </div>
          </div>

          <Button
            type="submit"
            disabled={saving}
            className="bg-[#9C6A48] hover:bg-[#8B5A33] text-white px-8"
            data-testid="save-content-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </form>
      </div>
    </div>
  );
}
